import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Lock, Sparkles, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SubscriptionPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [interval, setInterval] = useState("monthly");
  const [code, setCode] = useState("");
  const [busyPlan, setBusyPlan] = useState("");
  const [polling, setPolling] = useState(false);

  const load = async () => {
    try { setData((await api.get("/subscription")).data); } catch { setData(null); }
  };
  useEffect(() => { load(); }, []);

  // ---- Return-from-Stripe polling ----------------------------------------
  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    const cancelled = url.searchParams.get("checkout") === "cancelled";
    if (cancelled) {
      toast.info(t("billing.checkout_cancelled"));
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.toString());
    }
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;
    const poll = async () => {
      attempts++;
      try {
        const r = await api.get(`/billing/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          toast.success(t("billing.checkout_success"));
          setPolling(false);
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
          load();
          return;
        }
        if (["expired", "cancelled", "canceled"].includes(r.data.payment_status)) {
          toast.error(t("billing.checkout_failed"));
          setPolling(false);
          url.searchParams.delete("session_id");
          window.history.replaceState({}, "", url.toString());
          return;
        }
      } catch (e) {
        // Continue polling on transient errors
      }
      if (attempts >= maxAttempts) {
        setPolling(false);
        toast.info(t("billing.checkout_pending"));
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
        return;
      }
      setTimeout(poll, interval);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <div className="text-zinc-400">{t("common.loading")}</div>;

  const sub = data.subscription || {};
  const plans = data.plans || [];
  const currentPlan = sub.subscription_plan;
  const isOwner = user?.role === "owner";
  const statusKey = sub.subscription_status || "trial";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  const upgrade = async (planId) => {
    if (planId === "free") return;            // free has no checkout
    if (planId === currentPlan && statusKey === "active") {
      toast.info(t("billing.already_on_plan"));
      return;
    }
    try {
      setBusyPlan(planId);
      const r = await api.post("/billing/checkout", {
        plan_id: planId,
        billing_interval: interval,
        origin_url: window.location.origin,
        discount_code: code.trim() || null,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("no checkout url");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.checkout_error"));
      setBusyPlan("");
    }
  };

  return (
    <div className="space-y-6" data-testid="subscription-panel">
      {/* Current subscription card */}
      <div className="surface rounded-xl p-6 space-y-4" data-testid="subscription-current">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("billing.current_plan")}</div>
            <div className="flex items-center gap-3 mt-2">
              <h2 className="font-display text-2xl font-semibold capitalize">
                {t(`billing.plans.${currentPlan}`, { defaultValue: currentPlan })}
              </h2>
              <Badge
                data-testid="subscription-status-badge"
                className={
                  statusKey === "trial" ? "bg-amber-500/15 text-amber-300 border-amber-500/30" :
                  statusKey === "active" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
                  statusKey === "demo" ? "bg-blue-500/15 text-blue-300 border-blue-500/30" :
                  "bg-red-500/15 text-red-300 border-red-500/30"
                }
              >
                {t(`billing.statuses.${statusKey}`, { defaultValue: statusKey })}
              </Badge>
              {polling && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-300">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t("billing.checkout_processing")}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            {sub.trial_days_left != null && (
              <div className="text-sm text-zinc-400">
                <span className="text-amber-300 font-display text-2xl font-semibold">{sub.trial_days_left}</span>{" "}
                <span>{t("billing.trial_days_left")}</span>
              </div>
            )}
            {sub.trial_ends_at && (
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
                {t("billing.trial_ends")}: {fmtDate(sub.trial_ends_at)}
              </div>
            )}
            {sub.subscription_ends_at && statusKey === "active" && (
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
                {t("billing.renews_on")}: {fmtDate(sub.subscription_ends_at)}
              </div>
            )}
          </div>
        </div>

        {!isOwner && (
          <div className="text-xs text-zinc-500 inline-flex items-center gap-2">
            <Lock className="h-3 w-3" /> {t("billing.owner_only")}
          </div>
        )}
      </div>

      {/* Plan catalogue */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("billing.choose_plan")}</div>
          <div className="flex items-center gap-3">
            {isOwner && (
              <div className="flex items-center gap-2">
                <Label htmlFor="discount-input" className="text-xs text-zinc-500">{t("discount.code")}</Label>
                <Input
                  id="discount-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="WELCOME10"
                  className="bg-[#171B24] border-[#2E3241] w-36 h-8 text-xs uppercase"
                  data-testid="billing-discount-input"
                />
              </div>
            )}
            <div className="inline-flex bg-[#171B24] border border-[#2E3241] rounded-md p-0.5">
              {["monthly", "yearly"].map((iv) => (
                <button
                  key={iv}
                  type="button"
                  onClick={() => setInterval(iv)}
                  data-testid={`interval-${iv}`}
                  className={`text-xs px-3 py-1 rounded ${interval === iv ? "bg-blue-500 text-white" : "text-zinc-400 hover:text-white"}`}
                >
                  {t(`billing.${iv}`)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan;
            const price = interval === "monthly" ? p.monthly_price : p.yearly_price;
            const purchasable = isOwner && p.id !== "free" && (!isCurrent || statusKey !== "active");
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`surface rounded-xl p-5 flex flex-col ${isCurrent ? "ring-2 ring-blue-500/50" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold">
                    {t(`billing.plans.${p.id}`, { defaultValue: p.name })}
                  </h3>
                  {isCurrent && (
                    <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 text-[10px]">
                      <Check className="h-3 w-3 mr-1" /> {t("billing.current_plan")}
                    </Badge>
                  )}
                </div>
                <div className="mt-3">
                  <span className="font-display text-3xl font-semibold">€{price}</span>
                  <span className="text-xs text-zinc-500 ml-1">
                    {interval === "monthly" ? t("billing.per_month") : t("billing.per_year")}
                  </span>
                </div>
                <ul className="mt-3 space-y-1 text-xs text-zinc-400 flex-1">
                  {p.max_users != null && (
                    <li className="flex items-start gap-1.5">
                      <Check className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{t("billing.feat_users", { count: p.max_users })}</span>
                    </li>
                  )}
                  {p.max_contracts != null && (
                    <li className="flex items-start gap-1.5">
                      <Check className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{t("billing.feat_contracts", { count: p.max_contracts })}</span>
                    </li>
                  )}
                  {p.max_contracts == null && (
                    <li className="flex items-start gap-1.5">
                      <Check className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{t("billing.feat_unlimited_contracts")}</span>
                    </li>
                  )}
                  <li className="flex items-start gap-1.5">
                    <Check className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{t("billing.feat_all_features")}</span>
                  </li>
                </ul>
                {purchasable ? (
                  <Button
                    onClick={() => upgrade(p.id)}
                    disabled={busyPlan === p.id}
                    className="mt-4 bg-blue-500 hover:bg-blue-600 gap-2"
                    data-testid={`plan-upgrade-${p.id}`}
                  >
                    {busyPlan === p.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Sparkles className="h-3.5 w-3.5" />}
                    {t("billing.upgrade")}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto opacity-60" />
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="mt-4 bg-[#171B24] hover:bg-[#171B24] text-zinc-400 border border-[#2E3241] cursor-not-allowed gap-2"
                  >
                    {p.id === "free" ? t("billing.free_plan") : t("billing.current_plan")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
